import { useScrollReveal } from '../../hooks/useScrollReveal';

interface WhyChooseProps {
  items?: string[];
}

const defaultItems = [
  'Architecture centered on how you actually live',
  'Clean, modern residential design language',
  'Detail-focused execution — no shortcuts taken',
  'Personalized collaboration from concept to completion',
  'Designed for enduring value over decades',
  '20+ years of residential architecture expertise',
];

const icons = [
  'ri-heart-3-line',
  'ri-layout-masonry-line',
  'ri-medal-2-line',
  'ri-team-line',
  'ri-time-line',
  'ri-award-line',
];

export default function WhyChoose({ items = defaultItems }: WhyChooseProps) {
  const headerRef = useScrollReveal();

  return (
    <section className="py-36 bg-soft-white">
      <div className="max-w-7xl mx-auto px-8 lg:px-16">
        {/* Header */}
        <div ref={headerRef} className="reveal-on-scroll flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-20 mb-20">
          <div className="lg:w-5/12">
            <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze mb-4">The Difference</p>
            <h2 className="font-serif text-5xl lg:text-7xl font-light text-dark-ink leading-[1.0]">
              Why Talo<br />Architect
            </h2>
          </div>
          <div className="lg:w-7/12">
            <p className="font-sans text-base text-warm-gray leading-relaxed max-w-lg">
              We bring precision, passion, and over two decades of experience to every residential project — designing homes that serve life beautifully.
            </p>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-stone">
          {items.map((item, i) => (
            <WhyCard key={i} number={`0${i + 1}`} text={item} icon={icons[i % icons.length]} delay={i + 1} />
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyCard({
  number,
  text,
  icon,
  delay,
}: {
  number: string;
  text: string;
  icon: string;
  delay: number;
}) {
  const ref = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`reveal-on-scroll reveal-delay-${Math.min(delay, 6)} group relative p-10 border-r border-b border-stone bg-soft-white hover:bg-parchment transition-all duration-500 cursor-default overflow-hidden`}
    >
      {/* Bronze top accent bar */}
      <div className="absolute top-0 left-0 w-0 h-[2px] bg-bronze group-hover:w-full transition-all duration-700 ease-in-out" />

      {/* Large background number */}
      <span className="absolute bottom-4 right-6 font-serif text-8xl font-light text-stone/20 group-hover:text-bronze/10 transition-colors duration-500 select-none leading-none">
        {number}
      </span>

      <div className="relative z-10">
        {/* Icon with background */}
        <div className="mb-8">
          <div className="w-14 h-14 flex items-center justify-center rounded-full border border-stone bg-white group-hover:border-bronze/40 group-hover:bg-bronze/5 transition-all duration-500">
            <i className={`${icon} text-wood text-xl group-hover:text-bronze transition-colors duration-300`} />
          </div>
        </div>

        {/* Number label */}
        <p className="font-sans text-[10px] tracking-[0.22em] uppercase text-bronze/70 mb-3 group-hover:text-bronze transition-colors duration-300">
          {number}
        </p>

        {/* Text */}
        <p className="font-sans text-[15px] text-warm-gray leading-relaxed group-hover:text-dark-ink transition-colors duration-300 max-w-[230px]">
          {text}
        </p>
      </div>
    </div>
  );
}
